// SPDX-License-Identifier: AGPL-3.0-or-later

import {ATTACHMENT_MAX_HEIGHT} from '@app/features/messaging/utils/MediaDimensionConfig';

export const INLINE_MEDIA_GAP_PX = 4;
export const INLINE_MEDIA_VERY_WIDE_RATIO = 2;
export const INLINE_MEDIA_PORTRAIT_RATIO = 1.15;
export const INLINE_MEDIA_MAX_TILE_HEIGHT = ATTACHMENT_MAX_HEIGHT * 2;

export interface InlineMediaItem {
	width: number;
	height: number;
}

export interface InlineMediaTile {
	width: number;
	height: number;
	aspectRatio: string;
	columnSpan: 1 | 2 | 3;
}

export interface InlineMediaLayout {
	columns: 1 | 2 | 3;
	tiles: Array<InlineMediaTile>;
}

export interface PlanInlineMediaLayoutOptions {
	maxWidth: number;
	gap?: number;
	maxTileHeight?: number;
}

function isVeryWide(item: InlineMediaItem): boolean {
	return item.width > 0 && item.height > 0 && item.width / item.height >= INLINE_MEDIA_VERY_WIDE_RATIO;
}

function isPortrait(item: InlineMediaItem): boolean {
	return item.width > 0 && item.height > 0 && item.height / item.width >= INLINE_MEDIA_PORTRAIT_RATIO;
}

function columnWidth(maxWidth: number, columns: number, gap: number): number {
	return Math.max(1, (maxWidth - (columns - 1) * gap) / columns);
}

function containTile(
	item: InlineMediaItem,
	tileColumnWidth: number,
	maxTileHeight: number,
	columnSpan: 1 | 2 | 3,
): InlineMediaTile {
	if (!(item.width > 0) || !(item.height > 0)) {
		const size = Math.min(tileColumnWidth, maxTileHeight);
		return {width: size, height: size, aspectRatio: '1 / 1', columnSpan};
	}
	const unconstrainedHeight = (tileColumnWidth * item.height) / item.width;
	if (unconstrainedHeight > maxTileHeight) {
		const width = Math.max(1, (maxTileHeight * item.width) / item.height);
		return {
			width: Math.round(width),
			height: Math.round(maxTileHeight),
			aspectRatio: `${item.width} / ${item.height}`,
			columnSpan,
		};
	}
	return {
		width: Math.round(tileColumnWidth),
		height: Math.round(unconstrainedHeight),
		aspectRatio: `${item.width} / ${item.height}`,
		columnSpan,
	};
}

export function planInlineMediaLayout(
	items: ReadonlyArray<InlineMediaItem>,
	{maxWidth, gap = INLINE_MEDIA_GAP_PX, maxTileHeight = INLINE_MEDIA_MAX_TILE_HEIGHT}: PlanInlineMediaLayoutOptions,
): InlineMediaLayout {
	if (items.length === 0) {
		return {columns: 1, tiles: []};
	}
	const allPortrait = items.every(isPortrait);
	const columns: 1 | 2 | 3 = items.length >= 3 && allPortrait ? 3 : 2;
	const tileColumnWidth = columnWidth(maxWidth, columns, gap);
	return {
		columns,
		tiles: items.map((item) => {
			const fullRow = isVeryWide(item);
			const columnSpan = fullRow ? columns : 1;
			const width = fullRow ? maxWidth : tileColumnWidth;
			return containTile(item, width, maxTileHeight, columnSpan);
		}),
	};
}
